// PlanetCard - Detailed planet information modal card
import React from 'react';
import { motion } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { getPlanetByName, HOUSES } from '../../utils/astroConstants';
import { Button } from '../common/Button';
import { MarkdownText } from '../common/MarkdownText';

export const PlanetCard = ({ planet, onClose, onGetInsight, isLoading, insight }) => {
    if (!planet) return null;

    const planetMeta = getPlanetByName(planet.name);
    const houseMeta = HOUSES[planet.house - 1];

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
            {/* Backdrop */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Card */}
            <motion.div
                className="relative w-full max-w-md max-h-[85vh] bg-gradient-to-br from-purple-900/95 via-purple-950/95 to-indigo-950/95 border border-purple-500/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
                layoutId={`planet-${planet.name}`}
            >
                {/* Header */}
                <div
                    className="relative px-4 sm:px-6 py-4 sm:py-5 border-b border-purple-500/20 flex-shrink-0"
                    style={{
                        background: `linear-gradient(135deg, ${planetMeta?.color}20, transparent)`
                    }}
                >
                    <button
                        onClick={onClose}
                        className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 text-purple-300 hover:text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <div className="flex items-center gap-3 sm:gap-4">
                        <div
                            className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-2xl sm:text-3xl flex-shrink-0"
                            style={{
                                background: `radial-gradient(circle, ${planetMeta?.color}40, ${planetMeta?.color}10)`,
                                boxShadow: `0 0 30px ${planetMeta?.glowColor}`
                            }}
                        >
                            {planet.symbol}
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-xl sm:text-2xl font-serif text-amber-100">{planet.name}</h3>
                            <p className="text-purple-300 text-sm sm:text-base truncate">{planetMeta?.meaning}</p>
                        </div>
                    </div>
                </div>

                {/* Content - scrollable */}
                <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
                    {/* Position */}
                    <div className="p-3 sm:p-4 bg-purple-900/40 rounded-xl border border-purple-500/20">
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            <div>
                                <div className="text-purple-400/70 text-xs uppercase tracking-wide mb-1">Sign</div>
                                <div className="flex items-center gap-2">
                                    <span className="text-lg sm:text-xl">{planet.signSymbol}</span>
                                    <span className="text-amber-100 font-medium text-sm sm:text-base">{planet.sign}</span>
                                </div>
                            </div>
                            <div>
                                <div className="text-purple-400/70 text-xs uppercase tracking-wide mb-1">Position</div>
                                <div className="text-amber-100 text-sm sm:text-base">{planet.formattedPosition}</div>
                            </div>
                        </div>
                        <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-purple-500/20">
                            <div className="text-purple-400/70 text-xs uppercase tracking-wide mb-1">House</div>
                            <div className="flex items-center justify-between">
                                <span className="text-amber-100 font-medium text-sm sm:text-base">
                                    {planet.house}{getOrdinal(planet.house)} House
                                </span>
                                <span className="text-purple-300 text-xs sm:text-sm">{houseMeta?.name}</span>
                            </div>
                            <div className="text-purple-300/70 text-xs sm:text-sm mt-1">{houseMeta?.meaning}</div>
                        </div>
                    </div>

                    {/* Keywords */}
                    <div>
                        <div className="text-purple-400/70 text-xs uppercase tracking-wide mb-2">Key Themes</div>
                        <div className="flex flex-wrap gap-1.5 sm:gap-2">
                            {planetMeta?.keywords?.map((keyword, i) => (
                                <span
                                    key={i}
                                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-purple-800/50 border border-purple-500/30 rounded-full text-purple-200"
                                >
                                    {keyword}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* AI Insight */}
                    {insight ? (
                        <div className="p-3 sm:p-4 bg-amber-900/20 border border-amber-500/20 rounded-xl">
                            <div className="flex items-center gap-2 text-amber-400 text-xs sm:text-sm font-medium mb-2">
                                <Sparkles className="w-4 h-4" />
                                Cosmic Insight
                            </div>
                            <MarkdownText size="sm">
                                {insight}
                            </MarkdownText>
                        </div>
                    ) : (
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={() => onGetInsight(planet)}
                            loading={isLoading}
                            icon={Sparkles}
                            className="w-full border-amber-500/30"
                        >
                            {isLoading ? 'Consulting the stars...' : 'Get Deeper Insight'}
                        </Button>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

// Helper for ordinal suffix
const getOrdinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
};

export default PlanetCard;
